import React, { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Menu, Settings, FileText } from 'lucide-react';
import { Drawer } from '../../components/ui/Drawer';
import { ProductColumnToggle, DEFAULT_VISIBLE, ALL_COLUMNS, type ProductCardColumn } from '../../components/products/ProductColumnToggle';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPut, apiDelete, apiPatch } from '../../api/client';
import { Button } from '../../components/ui/Button';
import type { ProductTemplate } from '../../api/types';
import { TemplateModal } from './products/TemplateModal';
import { ProductCard } from './products/ProductCard';
import { useProductCategories } from '../../api/useProductCategories';
import { CategoryManagerModal } from '../../components/settings/CategoryManagerModal';
import { BulkPriceBar } from '../../components/products/BulkPriceBar';
import { ExportPdfDrawer } from '../../components/products/ExportPdfDrawer';
import { renderCatalogIcon } from '../../utils/renderCatalogIcon';

function marginPct(baseCost: number, basePrice: number): number {
  if (basePrice <= 0) return 0;
  return ((basePrice - baseCost) / basePrice) * 100;
}

export function ProductCatalogSection() {
  const qc = useQueryClient();

  // UI state
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ProductTemplate | null>(null);
  const [selectedCategorySlug, setSelectedCategorySlug] = useState<string>('album');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortBy, setSortBy] = useState<'margin' | 'price' | 'name'>('margin');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showExportDrawer, setShowExportDrawer] = useState(false);

  const [columnVisibility, setColumnVisibility] = useState<Record<ProductCardColumn, boolean>>(() => {
    try {
      const stored = localStorage.getItem('listino_columns_v1');
      if (stored) return { ...DEFAULT_VISIBLE, ...JSON.parse(stored) };
    } catch { /* ignore corrupt localStorage */ }
    return { ...DEFAULT_VISIBLE };
  });

  useEffect(() => {
    localStorage.setItem('listino_columns_v1', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  const hiddenColumns = useMemo<ProductCardColumn[]>(
    () => ALL_COLUMNS.filter(k => !columnVisibility[k]),
    [columnVisibility],
  );

  // API data
  const { data: templates = [] } = useQuery<ProductTemplate[]>({
    queryKey: ['product-templates'],
    queryFn: () => apiGet<ProductTemplate[]>('/product-templates'),
  });

  const { data: categoriesRaw = [] } = useProductCategories();

  // Deduplicate categories by slug (DB unique constraint)
  const categories = useMemo(() => {
    const seen = new Set<string>();
    return categoriesRaw.filter(c => {
      if (seen.has(c.slug)) return false;
      seen.add(c.slug);
      return true;
    });
  }, [categoriesRaw]);

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/product-templates/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['product-templates'] }); toast.success('Prodotto eliminato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => apiPost(`/product-templates/${id}/duplicate`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['product-templates'] }); toast.success('Prodotto duplicato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiPatch(`/product-templates/${id}`, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product-templates'] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkPriceMutation = useMutation({
    mutationFn: ({ ids, basePrice }: { ids: string[]; basePrice: number }) =>
      apiPost<{ updated: number }>('/product-templates/bulk-price', { ids, basePrice }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['product-templates'] });
      toast.success(`Prezzo aggiornato per ${data.updated} prodotti`);
      setSelectedIds(new Set());
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredProducts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const handleInlineSave = async (id: string, field: 'baseCost' | 'basePrice', value: number) => {
    await apiPatch(`/product-templates/${id}`, { [field]: value });
    qc.invalidateQueries({ queryKey: ['product-templates'] });
  };

  // Handlers
  const openCreate = () => { setEditing(null); setShowModal(true); };
  const openEdit   = (t: ProductTemplate) => { setEditing(t); setShowModal(true); };

  // Get the selected category object
  const selectedCategoryObj = categories.find(c => c.slug === selectedCategorySlug);

  // Filtered & sorted products for selected category
  const filteredProducts = useMemo(() => {
    if (!selectedCategoryObj) return [];

    // Match by category slug
    let result = templates.filter(t => t.category === selectedCategorySlug);

    // Filter by active status
    if (filterActive === 'active') {
      result = result.filter(t => t.isActive);
    } else if (filterActive === 'inactive') {
      result = result.filter(t => !t.isActive);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t =>
        t.name.toLowerCase().includes(q) ||
        (t.description?.toLowerCase().includes(q)) ||
        t.attributes.some(a => a.key.toLowerCase().includes(q))
      );
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === 'margin') {
        const aMargin = marginPct(a.baseCost, a.basePrice);
        const bMargin = marginPct(b.baseCost, b.basePrice);
        return bMargin - aMargin;
      }
      if (sortBy === 'price') {
        return b.basePrice - a.basePrice;
      }
      return a.name.localeCompare(b.name);
    });

    return result;
  }, [templates, selectedCategoryObj, filterActive, searchQuery, sortBy]);

  // Count products per category
  const categoryCounts: Record<string, number> = useMemo(() => {
    const counts: Record<string, number> = {};
    categories.forEach(cat => {
      counts[cat.id] = templates.filter(t => t.category === cat.slug).length;
    });
    return counts;
  }, [templates, categories]);

  // Sidebar content — JSX variable, not a component (used in both desktop sidebar and mobile drawer)
  const sidebarContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', height: '100%' }}>
      {/* Header */}
      <div>
        <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: '0.25rem' }}>Categorie</h3>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>Seleziona una categoria</p>
      </div>

      {/* Category List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', flex: 1, overflowY: 'auto' }}>
        {categories.length === 0 ? (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)', textAlign: 'center', padding: '1rem 0' }}>
            Nessuna categoria
          </p>
        ) : (
          categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => {
                setSelectedCategorySlug(cat.slug);
                setDrawerOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0.75rem',
                borderRadius: 'var(--radius-card)',
                background: selectedCategorySlug === cat.slug ? 'var(--color-primary)' : 'transparent',
                color: selectedCategorySlug === cat.slug ? 'white' : 'var(--color-text)',
                border: 'none',
                cursor: 'pointer',
                fontSize: 'var(--text-sm)',
                transition: 'all 0.15s ease',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => {
                if (selectedCategorySlug !== cat.slug) {
                  e.currentTarget.style.backgroundColor = 'var(--glass-bg-card)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedCategorySlug !== cat.slug) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span style={{ fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '18px' }}>
                {renderCatalogIcon(cat.icon, 18)}
              </span>
              <span style={{ flex: 1, fontWeight: selectedCategorySlug === cat.slug ? 700 : 500 }}>{cat.name}</span>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  background: selectedCategorySlug === cat.slug ? 'rgba(255,255,255,0.2)' : 'var(--glass-bg-card)',
                  padding: '0.125rem 0.375rem',
                  borderRadius: 'var(--radius-interactive)',
                }}
              >
                {categoryCounts[cat.id] ?? 0}
              </span>
            </button>
          ))
        )}
      </div>

      {/* Divider */}
      <div style={{ height: '1px', background: 'rgba(255,255,255,0.10)' }} />

      {/* Filter Section */}
      <div>
        <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-text-faint)', marginBottom: '0.375rem' }}>
          Stato
        </label>
        <select
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value as 'all' | 'active' | 'inactive')}
          style={{
            width: '100%',
            background: 'var(--glass-bg-card)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)',
            color: 'var(--color-text)',
            fontSize: 'var(--text-sm)',
            padding: '0.5rem 0.625rem',
            outline: 'none',
            cursor: 'pointer',
          }}
        >
          <option value="all">Tutti</option>
          <option value="active">Attivi</option>
          <option value="inactive">Inattivi</option>
        </select>
      </div>

      {/* Sort Section */}
      <div>
        <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-text-faint)', marginBottom: '0.375rem' }}>
          Ordina per
        </label>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'margin' | 'price' | 'name')}
          style={{
            width: '100%',
            background: 'var(--glass-bg-card)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)',
            color: 'var(--color-text)',
            fontSize: 'var(--text-sm)',
            padding: '0.5rem 0.625rem',
            outline: 'none',
            cursor: 'pointer',
          }}
        >
          <option value="margin">Margine</option>
          <option value="price">Prezzo</option>
          <option value="name">Nome</option>
        </select>
      </div>

      {/* Manage Categories Button */}
      <button
        onClick={() => setCategoryModalOpen(true)}
        title="Aggiungi, modifica, ordina o elimina categorie"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.375rem',
          padding: '0.625rem 0.875rem',
          background: 'var(--glass-bg-card)',
          border: '1px solid var(--color-primary)',
          borderRadius: 'var(--radius-card)',
          color: 'var(--color-primary)',
          cursor: 'pointer',
          fontSize: 'var(--text-xs)',
          fontWeight: 600,
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--color-primary)';
          e.currentTarget.style.color = 'white';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'var(--glass-bg-card)';
          e.currentTarget.style.color = 'var(--color-primary)';
        }}
      >
        <Settings size={14} />
        Gestisci categorie
      </button>
    </div>
  );

  // --- Render ---
  return (
    <>
      <style>{`
        .product-catalog-layout {
          background: var(--glass-bg-card);
          border: 1px solid oklch(from var(--color-text) l c h / 0.08);
          border-radius: var(--radius-card);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          min-height: auto;
        }

        .product-catalog-main {
          display: flex;
          gap: 0;
          flex: 1;
          min-height: 400px;
          overflow: hidden;
        }

        .product-catalog-sidebar {
          width: 220px;
          background: var(--glass-bg-card);
          border-right: 1px solid rgba(255,255,255,0.10);
          padding: 1rem;
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          overflow-y: auto;
          max-height: calc(100% - 100px);
        }

        .product-catalog-mobile-btn {
          display: none;
        }

        @media (max-width: 767px) {
          .product-catalog-sidebar {
            display: none;
          }
          .product-catalog-mobile-btn {
            display: flex;
          }
        }
      `}</style>
      <div className="product-catalog-layout">
      {/* Header - Full Width */}
      <div
        style={{
          padding: '0.875rem 1.25rem',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(255,255,255,0.04)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          Gestisci i tuoi prodotti fotografici con costo, prezzo e attributi
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Button size="sm" icon={<Plus size={14} />} onClick={openCreate}>
            Nuovo prodotto
          </Button>
          <Button size="sm" variant="secondary" icon={<FileText size={16} />} onClick={() => setShowExportDrawer(true)}>
            Esporta PDF
          </Button>
          <ProductColumnToggle
            visible={columnVisibility}
            onChange={(key, vis) => setColumnVisibility(prev => ({ ...prev, [key]: vis }))}
          />
          {/* Mobile hamburger */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="product-catalog-mobile-btn"
            style={{
              background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)',
              cursor: 'pointer',
              color: 'var(--color-text)',
              padding: '0.375rem 0.625rem',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Menu size={18} />
          </button>
        </div>
      </div>

      {/* Main Content - Desktop: flex row | Mobile: column with drawer */}
      <div className="product-catalog-main">
        {/* Sidebar - Desktop: visible | Mobile: drawer overlay */}
        <div className="product-catalog-sidebar">
          {sidebarContent}
        </div>


        {/* Right Panel - Content */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: '1rem',
            overflow: 'hidden',
          }}
        >
          {/* Search Bar */}
          <div
            style={{
              position: 'relative',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Search
              size={14}
              style={{
                position: 'absolute',
                left: '0.75rem',
                color: 'var(--color-text-muted)',
                pointerEvents: 'none',
              }}
            />
            <input
              type="text"
              placeholder="Cerca per nome, categoria, attributi…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                background: 'var(--glass-bg-card)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 'var(--radius-card)',
                color: 'var(--color-text)',
                fontSize: 'var(--text-sm)',
                padding: '0.625rem 0.75rem 0.625rem 2.25rem',
                outline: 'none',
              }}
            />
          </div>

          {/* Product Count */}
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginBottom: '0.75rem' }}>
            {filteredProducts.length} {filteredProducts.length === 1 ? 'prodotto' : 'prodotti'}
          </div>

          {/* Product List */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.375rem',
            }}
          >
            {filteredProducts.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  color: 'var(--color-text-faint)',
                  fontSize: 'var(--text-sm)',
                  textAlign: 'center',
                }}
              >
                {templates.length === 0
                  ? 'Nessun prodotto nel catalogo. Creane uno con il pulsante sopra.'
                  : 'Nessun prodotto corrisponde ai filtri.'}
              </div>
            ) : (
              <>
                {selectedIds.size > 0 && (
                  <div style={{ padding: '0.5rem' }}>
                    <BulkPriceBar
                      count={selectedIds.size}
                      onClear={() => setSelectedIds(new Set())}
                      onApplyBulkPrice={async (price) => {
                        await bulkPriceMutation.mutateAsync({ ids: [...selectedIds], basePrice: price });
                      }}
                      loading={bulkPriceMutation.isPending}
                    />
                  </div>
                )}
                {filteredProducts.map(t => (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <div style={{ paddingTop: '0.875rem' }} onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="dt-checkbox"
                        checked={selectedIds.has(t.id)}
                        onChange={() => toggleSelect(t.id)}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <ProductCard
                        template={t}
                        hiddenColumns={hiddenColumns}
                        isExpanded={expandedId === t.id}
                        onToggle={() => setExpandedId(expandedId === t.id ? null : t.id)}
                        onEdit={() => openEdit(t)}
                        onDuplicate={() => duplicateMutation.mutate(t.id)}
                        onDelete={() => deleteMutation.mutate(t.id)}
                        onToggleActive={() => toggleMutation.mutate({ id: t.id, isActive: !t.isActive })}
                        onSaveCost={async (v) => {
                          await handleInlineSave(t.id, 'baseCost', v);
                          toast.success('Costo aggiornato');
                        }}
                        onSavePrice={async (v) => {
                          await handleInlineSave(t.id, 'basePrice', v);
                          toast.success('Prezzo aggiornato');
                        }}
                      />
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <TemplateModal
        open={showModal}
        onClose={() => { setShowModal(false); setEditing(null); }}
        editing={editing}
      />
      <CategoryManagerModal
        isOpen={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        categories={categories}
        categoryCounts={categoryCounts}
      />
      <ExportPdfDrawer
        open={showExportDrawer}
        onClose={() => setShowExportDrawer(false)}
        products={filteredProducts.length > 0 ? filteredProducts : templates.filter(t => t.category === selectedCategorySlug)}
      />
      </div>
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Catalogo prodotti">
        {sidebarContent}
      </Drawer>
    </>
  );
}
